import { PAYMENT_METHOD } from './payment-method';

/**
 * Estados da conta de recebimento (plano, seção 5). Espelha
 * `provider_payment_account.status` — a T1.1 é quem define a entidade.
 */
export enum PAYMENT_ACCOUNT_STATUS {
  /** Dados incompletos: assistente começado e não terminado. */
  DRAFT = 'DRAFT',
  /** Enviado ao Stripe, ele está analisando. */
  VERIFYING = 'VERIFYING',
  /** Pode cobrar e receber repasse. */
  ACTIVE = 'ACTIVE',
  /** Falta algo, com prazo (`requirementsDue.currentDeadline`). */
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  /** Pagamentos ou repasses parados (intervenção de risco do Stripe). */
  RESTRICTED = 'RESTRICTED',
  /** Desligada. */
  DISABLED = 'DISABLED',
}

export enum PAYMENT_ACCOUNT_BUSINESS_TYPE {
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY = 'COMPANY',
}

/** Tipo de conta bancária pedido no passo 4 do assistente (seção 6.3). */
export enum PAYMENT_ACCOUNT_ACCOUNT_TYPE {
  CHECKING = 'CHECKING',
  SAVINGS = 'SAVINGS',
}

/** Formas que a conta pode oferecer — recorte de `PAYMENT_METHOD` sem `CASH`. */
export type PAYMENT_ACCOUNT_METHOD = Exclude<PAYMENT_METHOD, PAYMENT_METHOD.CASH>;

/**
 * O que o prestador escolheu oferecer no checkout automático, dentro do que
 * a subconta habilita — espelha `ProviderPaymentAccountAcceptedMethods` da
 * API. `null` até a escolha existir.
 */
export interface PaymentAccountAcceptedMethods {
  pix: boolean;
  card: boolean;
  boleto: boolean;
}

/**
 * `requirementsDue` da conta (seção 5): o que o Stripe ainda pede, e até
 * quando. Alimenta a tela de "Pendência" (seção 6.3) — cada item deveria
 * abrir a etapa certa do assistente, mas o texto de cada item vem do Stripe
 * (componente embutido), não daqui.
 */
export interface PaymentAccountRequirements {
  currentlyDue: string[];
  pastDue: string[];
  currentDeadline: string | null;
  disabledReason: string | null;
}

/**
 * O que a tela de Recebimentos, o card do Perfil e o assistente usam para
 * saber em que pé a conta está. Nunca carrega CPF, nascimento ou conta
 * bancária completa (seção 4.8): só o que já vem mascarado do Stripe.
 * Espelha `PaymentAccountEntryDto` (`api/src/payment-account/dto/payment-account-response.dto.ts`)
 * campo a campo — é a fonte da verdade; mudar um nome lá sem mudar aqui
 * quebra o contrato.
 */
export interface PaymentAccount {
  providerId: string;
  stripeAccountId: string;
  businessType: PAYMENT_ACCOUNT_BUSINESS_TYPE;
  status: PAYMENT_ACCOUNT_STATUS;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirementsDue: PaymentAccountRequirements | null;
  pixEnabled: boolean;
  cardEnabled: boolean;
  boletoEnabled: boolean;
  acceptedMethods: PaymentAccountAcceptedMethods | null;
  /**
   * `Provider.paymentCollection` (T2.4, exposto em `0380a3a`): se o checkout
   * automático está ligado agora. Não é dado sensível do dono — vai para
   * todo mundo que enxerga a conta, inclusive quem só acompanha
   * (`restricted`).
   */
  paymentCollection: PAYMENT_COLLECTION_MODE;
  /** `***.456.789-**` — nunca o CPF/CNPJ inteiro. */
  documentMasked: string | null;
  bankName: string | null;
  bankLast4: string | null;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  activatedAt: string | null;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pré-preenchimento do assistente de ativação (T4.3): sugestão, nunca
 * resposta pronta — `legalName` aparece para o prestador conferir, `bank` e
 * `phone` vêm preenchidos mas editáveis. Espelha `PaymentAccountPrefillDto`
 * campo a campo (fonte da verdade: `payment-account-response.dto.ts`).
 */
export interface PaymentAccountPrefill {
  legalName: string | null;
  email: string | null;
  phone: string | null;
  /** Faturamento médio mensal dos últimos 90 dias, em reais; `null` sem histórico. */
  suggestedMonthlyRevenue: number | null;
  bank: {
    bankName?: string;
    agency?: string;
    account?: string;
    accountDigit?: string;
  } | null;
}

/**
 * `POST /payment-account/session` (T1.5, plano seções 2.6/4.1/4.2, T4.4).
 * Espelha `PaymentAccountSessionResponseDto`
 * (`api/src/payment-account/dto/payment-account-session-response.dto.ts`)
 * campo a campo. Cada chamada cria uma `AccountSession` nova no Stripe — o
 * `clientSecret` **nunca** é logado nem guardado (nem em estado persistente,
 * nem em URL); ele só existe de passagem, entre o `POST` e o
 * `loadConnectAndInitialize`/`fetchClientSecret` do componente do Stripe.
 */
export interface PaymentAccountSession {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string;
  /** ISO 8601. */
  expiresAt: string;
}

/**
 * `GET /payment-account` (T1.4): `account` é `null` até o prestador concluir
 * o assistente (uma vez enviado, não existe mais estado "rascunho" do lado
 * do servidor — o envio é único, seção 2.5). `prefill` alimenta as sugestões
 * das etapas 1 e 4 do assistente e é `null` (bug de privacidade, revisão da
 * T1.4) para quem só pode ver o estado da conta, não geri-la — profissional
 * ou gerência num provider de caixa único que não é o dono: é quando o
 * assistente mostra "só o dono ativa" em vez do formulário.
 */
export interface PaymentAccountStatusResponse {
  account: PaymentAccount | null;
  prefill: PaymentAccountPrefill | null;
}

/** `Provider.paymentCollection` (seção 5) — liga/desliga o modo automático. */
export enum PAYMENT_COLLECTION_MODE {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC',
}

/**
 * `PATCH /payment-account/collection` (T2.4, contrato assumido).
 * `convertOpen`: ao ligar, oferece passar as cobranças em aberto para
 * automático; ao desligar, não se aplica (elas voltam sozinhas para manual).
 */
export interface UpdatePaymentCollectionPayload {
  mode: PAYMENT_COLLECTION_MODE;
  convertOpen?: boolean;
}

/**
 * `PATCH /payment-account/accepted-methods` (T6.4, PLANO 6.5). Espelha
 * `UpdateAcceptedMethodsDto` (`api/src/payment-account/dto/update-accepted-methods.dto.ts`):
 * substitui as três formas sempre juntas, exige pelo menos uma (validado no
 * servidor). Mesmo formato de `PaymentAccountAcceptedMethods` — o app só
 * reaproveita o tipo em vez de duplicar os três campos.
 */
export type UpdateAcceptedMethodsPayload = PaymentAccountAcceptedMethods;

// ---------------------------------------------------------------------------
// Pricing — GET /payment-account/pricing (T1.6)
// ---------------------------------------------------------------------------

/**
 * Chaves `payments.fee.*` de `platform_settings` (T1.6), uma por forma.
 * `cardFixed` e `boletoFixed` em reais; os `*Percent` em pontos percentuais
 * (1,19 = 1,19%), nunca em fração.
 */
export interface PaymentPricingFee {
  pixPercent: number;
  cardPercent: number;
  cardFixed: number;
  boletoFixed: number;
}

/** `payments.settlement.*` — dias até o valor ficar disponível para repasse. */
export interface PaymentPricingSettlement {
  cardDays: number;
  pixBusinessDays: number;
}

/**
 * Resposta de `GET /payment-account/pricing` (autenticado, T1.6). **Única**
 * fonte dos números mostrados no app — nenhuma tela ou texto escreve taxa,
 * prazo ou limite direto; tudo recebe este objeto por parâmetro.
 *
 * Contrato assumido (T1.6 ainda não existe): um objeto aninhado espelhando
 * as chaves `payments.*` de `platform_settings`, em camelCase. Ajustar este
 * tipo — e só ele — quando a T1.6 fechar o formato real.
 */
export interface PaymentPricing {
  /** `payments.enabled` — o recurso está ligado na plataforma. */
  enabled: boolean;
  /**
   * Se **este** prestador pode ativar agora (fora do enabled geral, cobre
   * `payments.beta_provider_ids` durante o beta e outras regras do plano,
   * como já estar em `RESTRICTED`).
   */
  canActivate: boolean;
  fee: PaymentPricingFee;
  /** `payments.margin_percent` — margem da Controlapag sobre a taxa (D1, 0% no lançamento). */
  marginPercent: number;
  /** `payments.pix_min`. */
  pixMin: number;
  /** `payments.pix_max` — `null` enquanto a T0.1 não confirmar um limite. */
  pixMax: number | null;
  /** `payments.boleto_min` (seed: 5). */
  boletoMin: number;
  /** `payments.boleto_max` (seed: 49999.99). */
  boletoMax: number;
  /** `payments.card_brands` — ex.: `['visa', 'mastercard']`. */
  cardBrands: string[];
  settlement: PaymentPricingSettlement;
  /** `payments.terms_version` — versão vigente dos termos de recebimento (seção 4.8, 6.3, `legal.tsx`). */
  termsVersion: string;
}

// ---------------------------------------------------------------------------
// Assistente de ativação — POST /payment-account (T1.4)
// ---------------------------------------------------------------------------

/** Etapa 2 do assistente (seção 6.3): endereço, com CEP resolvido pelo ViaCEP. */
export interface PaymentAccountAddress {
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

/** Etapa 4 do assistente (seção 6.3): conta para receber o repasse. */
export interface PaymentAccountBankDetails {
  bankCode: string;
  bankName: string;
  agency: string;
  account: string;
  accountDigit?: string;
  accountType: PAYMENT_ACCOUNT_ACCOUNT_TYPE;
}

/**
 * Rascunho salvo só no aparelho enquanto o prestador preenche o assistente
 * (seção 2.5, T4.3: "Rascunho só no aparelho, e só dos campos não
 * sensíveis"). **Não inclui CPF, data de nascimento nem a pergunta de PEP** —
 * esses só existem em memória até o envio único ao servidor.
 */
export interface PaymentAccountActivationDraft {
  businessType?: PAYMENT_ACCOUNT_BUSINESS_TYPE;
  address?: Partial<PaymentAccountAddress>;
  bank?: Partial<PaymentAccountBankDetails>;
}

/**
 * Corpo de `POST /payment-account` (T1.4): as 5 etapas respondidas, mandadas
 * **uma única vez** ao fim do assistente (seção 2.5 — "Nada vai ao servidor
 * antes do fim"). Espelha `CreatePaymentAccountDto` campo a campo (fonte da
 * verdade: `api/src/payment-account/dto/create-payment-account.dto.ts`). O
 * servidor repassa ao Stripe e devolve só o que pode ser lido de volta
 * (`PaymentAccount`, com CPF já mascarado); o app não guarda este payload
 * depois do envio.
 */
export interface PaymentAccountActivationPayload {
  businessType: PAYMENT_ACCOUNT_BUSINESS_TYPE;
  /** Como está no CPF/CNPJ — conferido na etapa 1, nunca persistido no app. */
  legalName: string;
  document: string;
  /** CNPJ, só quando `businessType === COMPANY`. */
  companyDocument?: string;
  /** ISO `yyyy-MM-dd`. */
  birthDate: string;
  address: PaymentAccountAddress;
  isPoliticallyExposed: boolean;
  bank: PaymentAccountBankDetails;
  /** Aceite dos **nossos** termos de recebimento (seção 4.8) — o do Stripe é aceito no componente embutido, etapa 6. */
  acceptedTermsVersion: string;
}
